import React, { useEffect, useState, FormEvent } from 'react';
import { tenancyApi, UserDto } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { useNavigate } from '../../router/Router';
import { Badge, formatDateTime } from '../../components/ui/Badge';
import { Dialog } from '../../components/ui/Dialog';
import { IconPlus, IconSettings, IconSync } from '../../components/ui/Icons';
import { PaginationBar } from '../../components/ui/PaginationBar';
import { useDebouncedValue, useServerPaginatedQuery } from '../../hooks/usePagination';

export function AdminUsersPage() {
  const { startImpersonation } = useAuth();
  const { pushToast } = useToast();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [loginAsFilter, setLoginAsFilter] = useState<'all' | 'allowed' | 'restricted'>('all');
  const [impersonatingId, setImpersonatingId] = useState<string | null>(null);

  // Create User Modal State
  const [createOpen, setCreateOpen] = useState<boolean>(false);
  const [newEmail, setNewEmail] = useState<string>('');
  const [newFullName, setNewFullName] = useState<string>('');
  const [newCompanyId, setNewCompanyId] = useState<string>('');
  const [creatingUser, setCreatingUser] = useState<boolean>(false);

  // Edit User Preferences Modal State
  const [editingUser, setEditingUser] = useState<UserDto | null>(null);
  const [editFullName, setEditFullName] = useState<string>('');
  const [editLoginAsAllowed, setEditLoginAsAllowed] = useState<boolean>(true);
  const [editDisableNotification, setEditDisableNotification] = useState<boolean>(false);
  const [savingUser, setSavingUser] = useState<boolean>(false);

  const isLoginAsAllowedParam =
    loginAsFilter === 'all' ? undefined : loginAsFilter === 'allowed';
  const debouncedSearch = useDebouncedValue(searchQuery, 250);
  const trimmedSearch = debouncedSearch.trim() || undefined;

  const usersQuery = useServerPaginatedQuery<UserDto>({
    queryKey: `admin-users:${loginAsFilter}:${trimmedSearch ?? ''}`,
    defaultPageSize: 10,
    fetchPage: (p, size) =>
      tenancyApi.getUsersDataTable({
        page: p,
        page_size: size,
        is_login_as_allowed: isLoginAsAllowedParam,
        search: trimmedSearch,
      }),
  });
  const isSearchingUsers =
    usersQuery.loading || searchQuery.trim() !== debouncedSearch.trim();

  useEffect(() => {
    if (usersQuery.error) {
      pushToast({
        tone: 'error',
        title: 'Failed to load users table',
        description: usersQuery.error,
      });
    }
  }, [usersQuery.error, pushToast]);

  const handleLoginAs = async (targetUser: UserDto) => {
    setImpersonatingId(targetUser.id);
    try {
      const res = await tenancyApi.loginAs({
        user_id: targetUser.id,
        ttl_secs: 3600,
      });
      await startImpersonation(res.token, `${res.user.full_name} (${res.user.email})`);
      pushToast({
        tone: 'success',
        title: `Impersonating ${res.user.email}`,
        description: 'Minted 1-hour support impersonation session.',
      });
      navigate('/projects');
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Impersonation denied',
        description:
          err instanceof Error
            ? err.message
            : 'Ensure Login-As access is enabled for this user.',
      });
    } finally {
      setImpersonatingId(null);
    }
  };

  const handleCreateUser = async (e: FormEvent) => {
    e.preventDefault();
    if (!newEmail.trim() || !newFullName.trim()) return;
    setCreatingUser(true);
    try {
      const created = await tenancyApi.createUser({
        email: newEmail.trim(),
        full_name: newFullName.trim(),
        company_id: newCompanyId.trim() || undefined,
      });
      await usersQuery.reload();
      setCreateOpen(false);
      setNewEmail('');
      setNewFullName('');
      setNewCompanyId('');
      pushToast({
        tone: 'success',
        title: 'User created',
        description: `${created.full_name} (${created.email}) registered.`,
      });
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Failed to create user',
        description: err instanceof Error ? err.message : 'Request failed',
      });
    } finally {
      setCreatingUser(false);
    }
  };

  const openEditUser = (u: UserDto) => {
    setEditingUser(u);
    setEditFullName(u.full_name);
    setEditLoginAsAllowed(u.is_login_as_allowed);
    setEditDisableNotification(u.disable_task_notification);
  };

  const handleSaveUserEdit = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;
    setSavingUser(true);
    try {
      await tenancyApi.updateUserPreferences(editingUser.id, {
        full_name: editFullName.trim() || undefined,
        is_login_as_allowed: editLoginAsAllowed,
        disable_task_notification: editDisableNotification,
      });
      await usersQuery.reload();
      setEditingUser(null);
      pushToast({
        tone: 'success',
        title: 'User preferences updated',
      });
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Failed to update user',
        description: err instanceof Error ? err.message : 'Request failed',
      });
    } finally {
      setSavingUser(false);
    }
  };

  const filteredUsers = usersQuery.data;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            Users &amp; Support Impersonation ({usersQuery.totalItems})
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Inspect user OAuth connections, manage preferences, or initiate a &ldquo;Login As&rdquo;
            session.
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={() => void usersQuery.reload()}
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-3.5 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            <IconSync className="w-4 h-4" />
            <span>Refresh</span>
          </button>

          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 px-4 py-2 text-xs font-semibold text-white hover:bg-indigo-700"
          >
            <IconPlus className="w-4 h-4" />
            <span>Create User</span>
          </button>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4">
        <div className="flex-1">
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              usersQuery.setPage(0);
            }}
            placeholder="Search users by name, email, company name, or ID…"
            className="w-full rounded-xl border border-slate-300 px-3.5 py-2 text-sm"
          />
        </div>
        <div className="flex items-center gap-2">
          <label
            htmlFor="login-as-allowed-filter"
            className="text-xs font-medium text-slate-600 whitespace-nowrap"
          >
            Login-As Allowed:
          </label>
          <select
            id="login-as-allowed-filter"
            value={loginAsFilter}
            onChange={(e) => {
              setLoginAsFilter(e.target.value as 'all' | 'allowed' | 'restricted');
              usersQuery.setPage(0);
            }}
            className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700"
          >
            <option value="all">All</option>
            <option value="allowed">Allowed</option>
            <option value="restricted">Restricted</option>
          </select>
        </div>
      </div>

      {/* Users Data Table */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-2xs overflow-hidden">
        <div className="table-scroll-container">
          <table className="min-w-full divide-y divide-slate-200 text-left text-xs">
            <thead className="bg-slate-50 text-slate-600 uppercase tracking-wider font-semibold">
              <tr>
                <th className="px-5 py-3.5">User</th>
                <th className="px-4 py-3.5">Company ID</th>
                <th className="px-4 py-3.5">OAuth Providers</th>
                <th className="px-4 py-3.5">Login-As Allowed</th>
                <th className="px-4 py-3.5">Last Login</th>
                <th className="px-5 py-3.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {isSearchingUsers ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-slate-500">
                    Loading users data table…
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-slate-500">
                    No users matched your filter.
                  </td>
                </tr>
              ) : (
                filteredUsers.map((u) => (
                  <tr key={u.id} className="hover:bg-slate-50">
                    <td className="px-5 py-4">
                      <div className="font-semibold text-slate-900">{u.full_name}</div>
                      <div className="text-slate-500">{u.email}</div>
                      <div className="font-mono text-[10px] text-slate-400">{u.id}</div>
                    </td>

                    <td className="px-4 py-4 font-mono text-slate-600">
                      {u.company_id ? `${u.company_id.slice(0, 8)}…` : '—'}
                    </td>

                    <td className="px-4 py-4">
                      <div className="flex flex-wrap gap-1">
                        <Badge
                          variant={u.procore_oauth?.connected ? 'success' : 'neutral'}
                          dot={Boolean(u.procore_oauth?.connected)}
                        >
                          Procore
                        </Badge>
                        <Badge
                          variant={u.smartsheet_oauth?.connected ? 'info' : 'neutral'}
                          dot={Boolean(u.smartsheet_oauth?.connected)}
                        >
                          Smartsheet
                        </Badge>
                      </div>
                    </td>

                    <td className="px-4 py-4">
                      {u.is_login_as_allowed ? (
                        <Badge variant="success">Allowed</Badge>
                      ) : (
                        <Badge variant="warning">Restricted</Badge>
                      )}
                    </td>

                    <td className="px-4 py-4 text-slate-600">{formatDateTime(u.last_login_at)}</td>

                    <td className="px-5 py-4 text-right">
                      <div className="inline-flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => openEditUser(u)}
                          className="rounded-lg border border-slate-300 bg-white p-1.5 text-slate-700 hover:bg-slate-50"
                          title="Edit User Preferences"
                        >
                          <IconSettings className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          disabled={!u.is_login_as_allowed || impersonatingId === u.id}
                          onClick={() => handleLoginAs(u)}
                          className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-40"
                          title={
                            u.is_login_as_allowed
                              ? 'Mint impersonation token and open Client Portal'
                              : 'User has disabled Login-As access'
                          }
                        >
                          {impersonatingId === u.id ? 'Minting…' : 'Login As →'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        <PaginationBar pagination={usersQuery} itemLabel="users" />
      </div>

      {/* Create User Modal */}
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Create Customer User"
        subtitle="Create a new user account."
      >
        <form onSubmit={handleCreateUser} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Full Name <span className="text-rose-600">*</span>
            </label>
            <input
              type="text"
              required
              value={newFullName}
              onChange={(e) => setNewFullName(e.target.value)}
              placeholder="Alex Morgan"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Email Address <span className="text-rose-600">*</span>
            </label>
            <input
              type="email"
              required
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="alex@construction.com"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Company UUID (optional)
            </label>
            <input
              type="text"
              value={newCompanyId}
              onChange={(e) => setNewCompanyId(e.target.value)}
              placeholder="00000000-0000-0000-0000-000000000000"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-xs font-mono"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setCreateOpen(false)}
              className="rounded-lg border border-slate-300 px-4 py-2 text-xs font-medium text-slate-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creatingUser}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {creatingUser ? 'Creating…' : 'Create User'}
            </button>
          </div>
        </form>
      </Dialog>

      {/* Edit User Preferences Modal */}
      <Dialog
        open={Boolean(editingUser)}
        onClose={() => setEditingUser(null)}
        title={`Edit User — ${editingUser?.email || ''}`}
        subtitle="Update user preferences."
      >
        {editingUser && (
          <form onSubmit={handleSaveUserEdit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Full Name</label>
              <input
                type="text"
                required
                value={editFullName}
                onChange={(e) => setEditFullName(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
            </div>

            <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <label className="flex items-center gap-2 text-xs font-medium text-slate-800">
                <input
                  type="checkbox"
                  checked={editLoginAsAllowed}
                  onChange={(e) => setEditLoginAsAllowed(e.target.checked)}
                />
                <span>Allow Admin &ldquo;Login As&rdquo; Impersonation (`is_login_as_allowed`)</span>
              </label>

              <label className="flex items-center gap-2 text-xs font-medium text-slate-800">
                <input
                  type="checkbox"
                  checked={editDisableNotification}
                  onChange={(e) => setEditDisableNotification(e.target.checked)}
                />
                <span>Disable Task Email Notifications (`disable_task_notification`)</span>
              </label>
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditingUser(null)}
                className="rounded-lg border border-slate-300 px-4 py-2 text-xs font-medium text-slate-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={savingUser}
                className="rounded-lg bg-indigo-600 px-4 py-2 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {savingUser ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </form>
        )}
      </Dialog>
    </div>
  );
}
